import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircleIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { Button } from "@babackapp/ui/components/button";
import logoColors from "@/assets/logo-colors.png";
import useTranslation from "@babackapp/translations/use-translation";
import { useAuth } from "@babackapp/auth-client/hooks";
import { toast } from "@babackapp/ui/components/sonner";

export default function SignUpSuccessPage() {
  const t = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const email = location.state?.email || "";
  
  const [countdown, setCountdown] = useState(10);
  const [isCountdownActive, setIsCountdownActive] = useState(true);
  const [hasResendBeenUsed, setHasResendBeenUsed] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (!isCountdownActive || countdown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, isCountdownActive]);

  const handleResendVerification = async () => {
    if (!email || hasResendBeenUsed || isCountdownActive) return;
    
    setIsResending(true);
    try {
      await auth.resendVerification(email);
      setHasResendBeenUsed(true);
      toast.success(t("signup.success.emailSent"), {
        description: t("signup.success.sentTo", { email })
      });
    } catch (error) {
      console.error("Failed to resend verification:", error);
      toast.error(t("verifyEmail.error.title"), {
        description: t("verifyEmail.error.description")
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-12 w-auto" src={logoColors} alt="Baback" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          {t("signup.success.title")}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
              <CheckCircleIcon className="h-8 w-8 text-green-600" aria-hidden="true" />
            </div>
            
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t("signup.success.subtitle")}
            </h3>
            
            <div className="flex items-center justify-center mb-4">
              <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2" aria-hidden="true" />
              <p className="text-sm text-gray-600">
                {email ? t("signup.success.sentTo", { email }) : t("signup.success.emailSent")}
              </p>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              {t("signup.success.description")}
            </p>
            
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                {t("signup.success.checkSpam")}
              </p>
              
              {!hasResendBeenUsed && (
                <Button
                  onClick={handleResendVerification}
                  variant="outline"
                  className="w-full"
                  disabled={isCountdownActive || isResending}
                >
                  {isCountdownActive 
                    ? `${t("verifyEmail.error.resend")} (${countdown}s)`
                    : isResending 
                      ? t("verifyEmail.error.resend")
                      : t("verifyEmail.error.resend")
                  }
                </Button>
              )}
              
              <div className="pt-4">
                <Link to="/auth/sign-in">
                  <Button variant="outline" className="w-full">
                    {t("verifyEmail.error.backToSignIn")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
